import { driveItemInfoType, accessInfoType } from "../utils/classInfos";
import axios from "axios";
import { PublicClientApplication } from '@azure/msal-browser';
import differenceInMinutes from "date-fns/differenceInMinutes";
import isValid from "date-fns/isValid";
import { config } from "../Config";
import store from "./StoreRoot";

var graph = require("@microsoft/microsoft-graph-client");
var publicClientApplication: PublicClientApplication;

export var globalGraphClient = undefined;
var globalSessionList = [];

// =================================================================
// DRIVE ITEMS
// =================================================================

export function getFileUrlFromDriveInfo(driveItemInfo: driveItemInfoType) {
  let fileUrl = driveItemInfo.fileUrl;
  return fileUrl;
}

export function getFileIdFromDriveInfo(driveItemInfo: driveItemInfoType) {
  return driveItemInfo.driveItemId;
}

// =================================================================
//
// ACCESS TOKENS
//
// =================================================================

export async function getAccessToken(scopes) {
  console.count("getAccessToken main");
  let askedScopes = scopes ?? store.main.userScopes;

  if (store.isTeams) {
    return getTeamsAccessToken();
  }

  try {
    publicClientApplication = store.workbooks.getClientApplication();
    const accounts = publicClientApplication
      .getAllAccounts();

    if (accounts.length <= 0) throw new Error('login_required');
    // Get the access token silently
    // If the cache contains a non-expired token, this function
    // will just return the cached token. Otherwise, it will
    // make a request to the Azure OAuth endpoint to get a token
    var silentResult = await publicClientApplication
      .acquireTokenSilent({
        scopes: askedScopes,
        account: accounts[ 0 ]
      });
    store.setAccessToken(silentResult, "graph", "");
    // console.log("INFO new AccessToken stored");
    return silentResult.accessToken;
  } catch (err) {
    // If a silent request fails, it may be because the user needs
    // to login or grant consent to one or more of the requested scopes
    if (isInteractionRequired(err)) {
      console.log("ERROR getAccessToken main", err);

      var interactiveResult = await publicClientApplication
        .acquireTokenPopup({
          scopes: askedScopes
        });

      return interactiveResult.accessToken;
    } else {
      throw err;
    }
  }
}

export async function getTeamsAccessToken() {

  let isTokenValid = (store.teams.teamsGraphToken?.access_token);
  // get a stored access token
  if (isTokenValid) {
    return store.teams.teamsGraphToken.access_token;
  }

  // get a new one
  let ssoToken = store.teams.teamsSsoToken;
  let tenantId = store.main.userTenantId;
  let graphToken;

  if (store.teams.teamsGraphErrors > 5) {
    console.log("too many ERRORS");
    return;
  }

  // get a new token
  console.info("INFO get new teams token");
  console.time("PROFILE checkTeamsAccessToken");
  try {
    let getConnectorUrl = 'https://app.airrange.io/api/getteamstoken';
    let res;
    try {
      res = await axios.get(getConnectorUrl + '?tenantId=' + tenantId + '&ssoToken=' + ssoToken);
    } catch (err) {
      console.log("ERROR axios getTeamsToken", err);
    }

    const resData = res?.data;
    graphToken = resData?.access_token;
    console.log("INFO resData", resData);
    if (graphToken) {
      // resData.scopes = getScopesAsList(resData.scope);
      store.teams.setTeamsGraphToken(resData);
      store.setAccessToken(resData, "teams", "");
      const hasUserFileAccess = store.getHasUserFileAccess();
      console.log("INFO hasUserFileAccess", hasUserFileAccess);
      store.workbooks.setUserHasFileAccess(hasUserFileAccess);
      // create a global client
      getGlobalAuthenticatedClient(graphToken, "teams");
    } else {
      console.log("INFO hasUserFileAccess false, no token");
      store.teams.setTeamsGraphError();
    }
  } catch (err) {
    console.log("ERROR checkTeamsAccessToken getAxios", err);
    store.teams.setTeamsGraphError();
  }
  console.timeEnd("PROFILE checkTeamsAccessToken");

  return graphToken;
}

export async function checkPublicAccessToken(accessInfo, sharingInfo) {
  const connectorId = accessInfo?.connectorId;
  if (!connectorId) return;
  let isTokenValid = false;

  // is a token info available
  let publicTokenValidUntil = sharingInfo?.connectorInfo?.tokenValidUntil;
  let publicAccessToken = sharingInfo?.connectorInfo?.tokenAccess;
  if (publicTokenValidUntil) {
    isTokenValid = isPublicTokenStillValid(publicTokenValidUntil);
  }

  // get a new token
  if (!isTokenValid) {
    console.info("INFO get new public token");
    console.time("PROFILE getPublicAccessToken-" + connectorId);
    try {
      let getConnectorUrl = 'https://app.airrange.io/api/connector';
      if (!store.isDebug) getConnectorUrl = 'api/connector';
      const apiKey = process.env.REACT_APP_API_KEY;
      const headers = { Authorization: `Bearer ${apiKey}` };
      const res = await axios.get(getConnectorUrl + '?connectorId=' + connectorId, { headers });
      const resData = res?.data;
      // TODO: is it perhaps encrypted?


      publicAccessToken = resData[ "accessToken" ];
      publicTokenValidUntil = resData[ "validUntil" ];
    } catch (err) {
      console.log("ERROR checkPublicAccessToken getAxios", err);
    }
    console.timeEnd("PROFILE getPublicAccessToken-" + connectorId);
  }
  if (!publicAccessToken) { return null; };

  let client;
  // do we need a session for this accessInfo
  const existingSession = getSessionFromDriveInfo(accessInfo.driveItemInfo);
  if (!existingSession) {
    // create a public client
    client = graph.Client.init({
      authProvider: (done: any) => {
        done(null, publicAccessToken);
      },
    });
    // we need to create a session entry - to prevent the public check
    await createSessionFromPublicDriveInfo(accessInfo, client, publicAccessToken, publicTokenValidUntil);
    // HACK - get the client out of the sessionList-accessToken
    // globalGraphClient = client;
  }

  return client;
}

function isPublicTokenStillValid(validUntil: string) {
  const validDate = new Date(validUntil);
  let isStillVaid = false;
  // check the token
  if (isValid(validDate)) {
    const now = new Date();
    if (now < validDate) {
      const validMinutes = differenceInMinutes(validDate, now);
      isStillVaid = validMinutes > 10;
    }
  }
  return isStillVaid;
}

function isInteractionRequired(error: Error): boolean {
  if (!error.message || error.message.length <= 0) {
    return false;
  }

  return (
    error.message.indexOf('consent_required') > -1 ||
    error.message.indexOf('interaction_required') > -1 ||
    error.message.indexOf('login_required') > -1 ||
    error.message.indexOf('no_account_in_silent_request') > -1 ||
    error.message.indexOf("renewal") > -1 ||
    error.message.indexOf("readwrite.all") > -1
  );
}

// =================================================================
//
// SESSIONS
//
// =================================================================

export async function getSessionForAccessInfo(accessInfo: accessInfoType) {
  const connectorId = accessInfo.connectorId;
  let sessionId;
  let client = globalGraphClient;

  if (connectorId) {
    const session = getSessionFromDriveInfo(accessInfo.driveItemInfo);
    if (!session) return null;
    sessionId = session.sessionId;
    // create a public client
    if (session.accessToken) {
      client = graph.Client.init({
        authProvider: (done: any) => {
          done(null, session.accessToken);
        },
      });
    }

  } else {
    // no external data
    sessionId = await getSessionIdFromDriveInfo(accessInfo.driveItemInfo);
  }
  const fileUrl = getFileUrlFromDriveInfo(accessInfo.driveItemInfo);
  if (!sessionId || !fileUrl || !client) {
    console.log("ERROR no session for accessInfo: connectorId=" + connectorId, client);
    return null;
  }

  // start the timer
  setLastGraphAccess();

  return {
    client: client,
    sessionId: sessionId,
    fileUrl: fileUrl
  };
}

export async function getSessionIdFromDriveInfo(driveItemInfo: driveItemInfoType, createIfNotExists: boolean = true) {
  if (!driveItemInfo) return;
  // console.count("getSessionIdFromDriveInfo");

  const fileId = getFileIdFromDriveInfo(driveItemInfo);
  const fileUrl = getFileUrlFromDriveInfo(driveItemInfo);
  const timeNow = new Date();
  let sessionId = null;
  let foundSession = null;

  // refresh the accessToken
  if (!store.getIsAccessTokenStillValid()) {
    console.log("INFO accessToken expired");
    // clear the session list
    globalSessionList = [];
  }

  // look for an existing session
  foundSession = globalSessionList.find(obj => {
    return obj.fileId === fileId;
  });
  if (foundSession) {
    // check the timestamp
    sessionId = foundSession.sessionId;
    const timeStamp = foundSession.timeStamp;
    const sessionAgeInMinutes = differenceInMinutes(timeNow, timeStamp);
    if (sessionAgeInMinutes > 4) {
      // refresh the session
      console.log("SESSION found but session too old", sessionAgeInMinutes);
      globalSessionList.length = 0;
      // const result = await refreshSession(foundSession, driveItemInfo, globalGraphClient);
    } else {
      console.log("SESSION found");
      foundSession.timeStamp = timeNow;

      return sessionId;
    }
  }

  if (createIfNotExists === false) return null;
  // get a new one
  try {
    let sessionInfo = null;

    // create a new session
    console.count("INFO getSessionIdFromDriveInfo-new");
    console.time('PROFILE getSession-' + fileId);
    const workbookSessionInfo = { persistChanges: true };

    if (!globalGraphClient) return "";

    await getGlobalAuthenticatedClient(null, null);

    sessionInfo = await globalGraphClient
      .api(fileUrl + "/workbook/createSession")
      .post(workbookSessionInfo)
      .catch(error => {
        const errorCode = error.code;
        if (errorCode === "UnsupportedFeatureEditWarning") {
          console.log("ERROR UnsupportedFeatureEditWarning", error.message, error);
        } else if (errorCode === "EditModeAccessDenied") {
          console.log("ERROR EditModeAccessDenied", error.message, error);
        } else {
          console.error('ERROR createSession', error.name, error.message, error);
        }
      });
    if (sessionInfo) {
      // create a new session entry
      const newSession = {
        fileId: fileId,
        sessionId: sessionInfo.id,
        timeStamp: timeNow
      };
      globalSessionList.push(newSession);
      // start the timer
      setLastGraphAccess();
    }
    console.timeEnd('PROFILE getSession-' + fileId);

    return sessionInfo?.id;
  } catch (err) {
    console.log("ERROR getSession", err.message);
    console.log(
      "ERROR getSession fileUrl",
      fileUrl,
      driveItemInfo
    );
  }
}

export function getSessionFromDriveInfo(driveItemInfo: driveItemInfoType) {
  if (!driveItemInfo) return false;

  const fileId = getFileIdFromDriveInfo(driveItemInfo);
  const timeNow = new Date();
  let foundSession = null;

  // look for an existing session
  foundSession = globalSessionList.find(obj => {
    return obj.fileId === fileId;
  });
  if (foundSession) {
    // check the timestamp
    const timeStamp = foundSession.timeStamp;
    const sessionAgeInMinutes = differenceInMinutes(timeNow, timeStamp);
    if (sessionAgeInMinutes > 4) {
      console.log("SESSION found but session too old", sessionAgeInMinutes);
      // globalSessionList.length = 0;
      foundSession = null;
    } else {
      console.log("SESSION found");
      foundSession.timeStamp = timeNow;
    }
  }
  return (foundSession);
}

export async function createSessionFromPublicDriveInfo(accessInfo: any, client: any, accessToken: string, tokenValidUntil: any) {
  if (!accessInfo.driveItemInfo) return;

  const fileId = getFileIdFromDriveInfo(accessInfo.driveItemInfo);
  const fileUrl = getFileUrlFromDriveInfo(accessInfo.driveItemInfo);
  const timeNow = new Date();

  try {
    let sessionInfo = null;

    // create a new session
    console.count("createSessionFromPublicDriveInfo");
    console.time("PROFILE getPublicSession-" + fileId);
    const workbookSessionInfo = { persistChanges: true };

    if (!client) return "";
    sessionInfo = await client
      .api(fileUrl + "/workbook/createSession")
      .post(workbookSessionInfo)
      .catch(error => {
        const errorCode = error.code;
        if (errorCode === "UnsupportedFeatureEditWarning") {
          console.log("ERROR UnsupportedFeatureEditWarning", error.message, error);
        } else {
          console.error('ERROR createPublicSession', error.name, error.message, error);
        }
      });
    if (sessionInfo) {
      // create a new session entry
      const newSession = {
        fileId: fileId,
        sessionId: sessionInfo.id,
        connectorId: accessInfo.connectorId,
        timeStamp: timeNow,
        accessToken: accessToken,
        tokenValidUntil: tokenValidUntil
      };
      globalSessionList.push(newSession);
    }
    console.timeEnd("PROFILE getPublicSession-" + fileId);

    return sessionInfo?.id;
  } catch (err) {
    console.log("ERROR createSessionFromPublicDriveInfo", err.message);
  }
}

// export async function refreshSession(foundSession, driveItemInfo: driveItemInfoType, client: any) {
//   if (!driveItemInfo || !foundSession || !client) return;
//   const fileUrl = getFileUrlFromDriveInfo(driveItemInfo);
//   const sessionId = foundSession.sessionId;

//   const workbookSessionInfo = { };
//   const sessionRefresh = client
//     .api(fileUrl + "/workbook/refreshSession")
//     .header("workbook-session-id", sessionId)
//     .post(workbookSessionInfo)
//     .then((data) => {
//       console.log("SESSION refreshed", data);
//       // update the timestamp
//       // foundSession.timeStamp = timeNow;
//       return sessionId;
//     })
//     .catch((err) => {
//       console.log("ERROR happened refreshing session - get a new one!", err);
//       globalSessionList = [];
//       return null;
//     });
//   console.log("SESSION refresh", sessionRefresh, globalSessionList);
// };

// =================================================================
//
// GRAPH
//
// =================================================================

export function checkGraphResponse(response, location) {
  if (!response) {
    return false;
  }

  // single result
  if (response.hasOwnProperty("status")) {
    if ([ 200, 201 ].includes(response.status) === true) {
      return true;
    }
  }

  // multiple results
  let statusIsOk = true;
  if (response.hasOwnProperty("responses")) {
    response.responses.forEach((res, index) => {
      if (![ 200, 201 ].includes(res.status)) {
        statusIsOk = false;
        if (res.status === 429) {
          console.log("ERROR THROTTLING", location, res);
        }
      }
    });
  }

  if (!statusIsOk) {
    console.log("ERROR GRAPH RESPONSES " + location, response.responses);
    return false;
  }

  // if ([200, 201].includes(response.status) === false) {
  //   console.log("ERROR with graph response: " + location, response.status);
  //   if (response.status === 429) {
  //     console.log("ERROR THROTTLING", location, response);
  //   }
  //   return false;
  // }
  return true;
}

// function checkLastGraphAccess() {
//   const timeNow = new Date();
//   const lastAccessAgeInMinutes = differenceInMinutes(timeNow, lastGraphAccess);
//   return (lastAccessAgeInMinutes < 5);
// }

function setLastGraphAccess() {
  // lastGraphAccess = new Date();
}

// =================================================================
//
// FUNCTIONS
//
// =================================================================

export function getGraphClientFromAccessToken(accessToken: string, connectorId: string) {
  if (!accessToken) return;
  // check the accessToken

  // create a public client
  const client = graph.Client.init({
    authProvider: (done: any) => {
      done(null, accessToken);
    },
  });
  return client ?? globalGraphClient;
}

export async function getGlobalAuthenticatedClient(accessToken: string, accessType: string, force: boolean = false) {

  if (!accessToken) {
    // reset client if null
    globalGraphClient = null;
  }

  if (!globalGraphClient || force) {
    // get a new accessToken if null
    let actualToken = accessToken;
    if (!actualToken) {
      actualToken = await getAccessToken(config.scopes);
    };
    // Initialize Graph client
    globalGraphClient = graph.Client.init({
      // defaultVersion: 'beta',
      // Use the provided access token to authenticate
      // requests
      authProvider: (done: any) => {
        done(null, actualToken);
      },
    });
  }
  return globalGraphClient;
}

// ====================================
// OTHER
// ====================================

export async function getMyGraphUserDetails() {
  let user = store.main.userMe;
  // load user info
  if (!user) {
    try {
      user = await globalGraphClient
        .api("/me")
        .get();
      store.main.setMicrosoftUser(user);
    } catch (err) {
      console.log("ERROR getMyUserDetails", err);
    }
  }

  // load group info
  // try {
  //   let member = await globalGraphClient
  //     .api("/me/memberOf")
  //     .get();
  // } catch (err) {
  //   console.log("ERROR getMyUserDetails members", err);
  // }

  let photo = store.main.getUserPhoto();
  // load user photo
  if (!photo && !store.main.noUserPhoto) {
    try {
      photo = await globalGraphClient
        .api("/me/photo/$value")
        .get();
      if (photo) {
        store.main.setUserPhoto(photo);
      }
    } catch (err) {
      console.log("ERROR getMyUserDetails photo", err);
      store.main.setNoUserPhoto(true);
    }
  }
  return { user, photo };
}

export async function getMyDriveDetail() {
  let data = {};
  try {
    if (!globalGraphClient) {
      console.log("getMyDriveDetail: globalGraphClient is null");
      return data;
    }
    data = await globalGraphClient.api('/me/drive/root').get();
  } catch (error) {
    console.log('Get drive detail: ', error);
  }

  return data;
}
